(function () {
  'use strict';

  class AccessibleCarouselV2 {
    constructor(element) {
      this.carouselElement = element;
      this.listContainer = this.carouselElement.querySelector('.accessible-carousel__list-container');
      this.list = this.carouselElement.querySelector('.accessible-carousel__list');
      this.items = Array.from(this.carouselElement.querySelectorAll('.accessible-carousel__item'));
      this.prevButton = this.carouselElement.querySelector('.accessible-carousel__button--prev');
      this.nextButton = this.carouselElement.querySelector('.accessible-carousel__button--next');
      this.progressWrap = this.carouselElement.querySelector('.accessible-carousel__progress');
      this.progressThumb = this.carouselElement.querySelector('.accessible-carousel__progress-thumb');

      if (!this.list || !this.listContainer || this.items.length === 0) {
        if (this.prevButton) this.prevButton.style.display = 'none';
        if (this.nextButton) this.nextButton.style.display = 'none';
        if (this.progressWrap) this.progressWrap.classList.add('is-hidden');
        console.warn('Carousel not initialized: missing elements or items for section', this.carouselElement.dataset.sectionId);
        return;
      }

      this.currentIndex = 0;
      this.init();
    }

    getItemsPerView() {
      const styles = getComputedStyle(this.carouselElement);
      let items = 1; // Default
      if (window.matchMedia('(min-width: 990px)').matches) {
        items = parseInt(styles.getPropertyValue('--items-desktop').trim()) || 3;
      } else if (window.matchMedia('(min-width: 750px)').matches) {
        items = parseInt(styles.getPropertyValue('--items-tablet').trim()) || 2;
      } else {
        items = parseInt(styles.getPropertyValue('--items-mobile').trim()) || 1;
      }
      return items;
    }

    updateButtonStates() {
      if (!this.prevButton || !this.nextButton) return;
      const itemsPerView = this.getItemsPerView();

      this.prevButton.disabled = this.currentIndex === 0;
      // Disable next if the current index is such that the last 'page' of items is already visible
      this.nextButton.disabled = this.currentIndex >= this.items.length - itemsPerView;

      // Special case: if total items is less than or equal to itemsPerView, disable both
      if (this.items.length <= itemsPerView) {
        this.prevButton.disabled = true;
        this.nextButton.disabled = true;
      }
    }

    updateProgress() {
      if (!this.progressThumb || !this.progressWrap || !this.list) return;
      const maxScroll = this.list.scrollWidth - this.list.clientWidth;
      if (maxScroll <= 1) {
        this.progressWrap.classList.add('is-hidden');
        return;
      }
      this.progressWrap.classList.remove('is-hidden');
      const wPct = (this.list.clientWidth / this.list.scrollWidth) * 100;
      const leftPct = (this.list.scrollLeft / maxScroll) * (100 - wPct);
      this.progressThumb.style.width = wPct + '%';
      this.progressThumb.style.left = leftPct + '%';
    }

    scrollToCurrentIndex(behavior = 'smooth') {
      if (this.items[this.currentIndex]) {
        const scrollTarget = this.items[this.currentIndex].offsetLeft;

        this.list.scrollTo({
          left: scrollTarget,
          behavior: behavior
        });
      }
      this.updateButtonStates();
      if (behavior === 'auto') {
        requestAnimationFrame(() => this.updateProgress());
      }
    }

    handlePrevClick() {
      this.currentIndex = Math.max(0, this.currentIndex - 1);
      this.scrollToCurrentIndex();
    }

    handleNextClick() {
      const itemsPerView = this.getItemsPerView();
      this.currentIndex = Math.min(this.items.length - itemsPerView, this.currentIndex + 1);
      this.scrollToCurrentIndex();
    }

    handleScroll() {
      const listScrollLeft = this.list.scrollLeft;
      const containerWidth = this.listContainer.offsetWidth;
      let newCurrentIndex = 0;
      let minDistance = Infinity;
      for (let i = 0; i < this.items.length; i++) {
        const item = this.items[i];
        const distance = Math.abs(item.offsetLeft - listScrollLeft);
        if (distance < minDistance) {
          minDistance = distance;
          newCurrentIndex = i;
        } else {
          break;
        }
      }

      if (this.currentIndex !== newCurrentIndex) {
        this.currentIndex = newCurrentIndex;
      }
      this.updateButtonStates();
    }

    handleResize() {
      // Ensure the current index is valid after items per view might have changed
      const itemsPerView = this.getItemsPerView();
      if (this.currentIndex > this.items.length - itemsPerView && this.items.length > itemsPerView) {
        this.currentIndex = Math.max(0, this.items.length - itemsPerView);
      } else if (this.items.length <= itemsPerView) {
        this.currentIndex = 0; // Reset to start if all items fit
      }
      this.scrollToCurrentIndex('auto');
      this.updateButtonStates(); // Call explicitly after resize adjustment
      this.updateProgress();
    }

    init() {
      if (this.prevButton) {
        this.prevButton.addEventListener('click', this.handlePrevClick.bind(this));
      }
      if (this.nextButton) {
        this.nextButton.addEventListener('click', this.handleNextClick.bind(this));
      }

      let scrollTimeout;
      this.list.addEventListener('scroll', () => {
        this.updateProgress();
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => this.handleScroll(), 150); // Debounce/throttle
      });

      let resizeTimeout;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => this.handleResize(), 200);
      });

      this.handleResize(); // Call on init to set correct states and positions
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    const carouselElements = document.querySelectorAll('.accessible-carousel');
    carouselElements.forEach((carouselEl) => {
      if (!carouselEl.classList.contains('js-initialized')) {
        // Prevent double initialization
        new AccessibleCarouselV2(carouselEl);
        carouselEl.classList.add('js-initialized');
      }
    });
  });
})();
